import myAxios from "./axiositeachWrapper";
import Config from "../_config/config";

/**
 * LessonPlanCoverageReportService — read-only client for the nodefork
 * lesson-plan coverage report (port of Java lessonPlan.do coverage report).
 * All calls are GETs; nothing mutates.
 */

export interface Option {
  id: number;
  name: string;
}

/** A filter topic, tagged with its owning course so identical names stay distinguishable. */
export interface TopicOption extends Option {
  courseId: number;
  courseName: string;
}

export interface CoverageFilters {
  schools: Option[];
  courses: Option[];
  years: Option[];
  topics: TopicOption[];
  batches: Option[];
  /** Instructors who actually taught in the selected centre(s) + course(s). */
  instructors: Option[];
}

export interface CellMetrics {
  planned: number;
  covered: number;
  difference: number;
  p: number;
  held: number;
  exam: number;
  swapped: number;
  additional: number;
  cancelled: number;
  holiday: number;
  totalHeld: number;
  diffPH: number;
}

export interface CoverageBatch {
  batchId: number;
  batchName: string;
  instructor: string;
  instructorPerTopic: Record<string, string>;
  perTopic: Record<string, CellMetrics>;
  overall: CellMetrics;
}

export interface CoverageCentre {
  schoolId: number;
  schoolName: string;
  batches: CoverageBatch[];
  total: { perTopic: Record<string, CellMetrics>; overall: CellMetrics };
}

export interface CourseReport {
  courseId: number;
  courseName: string;
  topics: Option[];
  centres: CoverageCentre[];
}

export interface CoverageReport {
  reportBy: 0 | 1;
  courses: CourseReport[];
}

export interface CoverageQuery {
  schoolIds?: number[];
  courseIds: number[];
  yearId?: number;
  topicIds?: number[];
  batchIds?: number[];
  instructorIds?: number[];
  reportBy?: 0 | 1;
  fromDate?: string;
  toDate?: string;
}

class LessonPlanCoverageReportService {
  private base = `${Config.nodeSiteUrl}v1/reports/lesson-plan-coverage`;

  private toParams(q: Partial<CoverageQuery>): URLSearchParams {
    const p = new URLSearchParams();
    if (q.courseIds?.length) p.set("courseIds", q.courseIds.join(","));
    if (q.yearId) p.set("yearId", String(q.yearId));
    if (q.reportBy !== undefined) p.set("reportBy", String(q.reportBy));
    if (q.schoolIds?.length) p.set("schoolIds", q.schoolIds.join(","));
    if (q.topicIds?.length) p.set("topicIds", q.topicIds.join(","));
    if (q.batchIds?.length) p.set("batchIds", q.batchIds.join(","));
    if (q.instructorIds?.length) p.set("instructorIds", q.instructorIds.join(","));
    if (q.fromDate) p.set("fromDate", q.fromDate);
    if (q.toDate) p.set("toDate", q.toDate);
    return p;
  }

  /** Cascading filter options. Pass current courses/school/year to refine topics/batches/instructors. */
  async getFilters(sel?: { courseIds?: number[]; schoolIds?: number[]; yearId?: number }): Promise<CoverageFilters> {
    const p = new URLSearchParams();
    if (sel?.courseIds?.length) p.set("courseIds", sel.courseIds.join(","));
    if (sel?.yearId) p.set("yearId", String(sel.yearId));
    if (sel?.schoolIds?.length) p.set("schoolIds", sel.schoolIds.join(","));
    const res = await (myAxios as any).get(`${this.base}/filters?${p.toString()}`);
    return res?.data?.data ?? { schools: [], courses: [], years: [], topics: [], batches: [], instructors: [] };
  }

  async getReport(q: CoverageQuery): Promise<CoverageReport> {
    const res = await (myAxios as any).get(`${this.base}?${this.toParams(q).toString()}`);
    return res?.data?.data ?? { reportBy: q.reportBy ?? 0, courses: [] };
  }

  async exportReport(q: CoverageQuery): Promise<Blob> {
    const res = await (myAxios as any).get(`${this.base}/export?${this.toParams(q).toString()}`, {
      responseType: "blob",
    });
    return res.data;
  }
}

export default new LessonPlanCoverageReportService();
